import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../../lib/graphql';
import {
  Card,
  CardContent,
  Text,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '@tonic/shared/components/ui';
import { Search, Users, Plus } from 'lucide-react';

const GET_CUSTOMERS = `
  query GetCustomers($search: String, $filter: CustomerFilter, $pagination: PaginationInput) {
    customers(search: $search, filter: $filter, pagination: $pagination) {
      items {
        id
        firstName
        lastName
        email
        loyaltyTier
        totalOrders
        lifetimeValue
        lastOrderDate
        reCommerceActivity {
          totalReturns
          totalRepairs
          totalResales
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

const CREATE_CUSTOMER = `
  mutation CreateCustomer($input: CreateCustomerInput!) {
    createCustomer(input: $input) {
      success
      customer {
        id
        firstName
        lastName
        email
        loyaltyTier
      }
      error {
        message
      }
    }
  }
`;

const LOYALTY_TIERS = ['ALL', 'STANDARD', 'SILVER', 'GOLD', 'VIP'];

export function CustomersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    email: '',
    firstName: '',
    lastName: '',
    marketingOptIn: false
  });
  const pageSize = 10;

  console.log('=== Customers Page Query Parameters ===');
  console.log('Search:', search);
  console.log('Filter:', filter);
  console.log('Page:', page);
  console.log('PageSize:', pageSize);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', { search, filter, page, pageSize }],
    queryFn: () => fetchGraphQL(GET_CUSTOMERS, {
      search: search || undefined,
      filter: filter === 'all' ? undefined : {
        loyaltyTier: filter
      },
      pagination: {
        page,
        limit: pageSize
      }
    }),
  });

  const createCustomerMutation = useMutation({
    mutationFn: (input) => fetchGraphQL(CREATE_CUSTOMER, { input }),
    onSuccess: () => {
      setIsCreateDialogOpen(false);
      refetch();
      setNewCustomer({
        email: '',
        firstName: '',
        lastName: '',
        marketingOptIn: false
      });
    }
  });

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handleLoyaltyTierChange = (value) => {
    setFilter(value);
    setPage(1);
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    await createCustomerMutation.mutate(newCustomer);
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-red-600">
        Error loading customers
      </div>
    );
  }

  const { items: customers = [], totalCount = 0 } = data?.customers || {};
  const hasNextPage = (page * pageSize) < totalCount;
  const hasPreviousPage = page > 1;
  const startItem = ((page - 1) * pageSize) + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Text variant="h1">Customers</Text>
          <Text variant="body" color="muted">Manage and view customer information</Text>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="secondary" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {totalCount.toLocaleString()} total customers
          </Badge>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Customer
          </Button>
        </div>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
            <DialogDescription>
              Add a new customer to the system. All fields are required.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCustomer}>
            <div className="space-y-4 py-4">
              <FormField name="email">
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer(prev => ({
                        ...prev,
                        email: e.target.value
                      }))}
                      required
                    />
                  </FormControl>
                </FormItem>
              </FormField>
              <FormField name="firstName">
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      value={newCustomer.firstName}
                      onChange={(e) => setNewCustomer(prev => ({
                        ...prev,
                        firstName: e.target.value
                      }))}
                      required
                    />
                  </FormControl>
                </FormItem>
              </FormField>
              <FormField name="lastName">
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      value={newCustomer.lastName}
                      onChange={(e) => setNewCustomer(prev => ({
                        ...prev,
                        lastName: e.target.value
                      }))}
                      required
                    />
                  </FormControl>
                </FormItem>
              </FormField>
              <FormField name="marketingOptIn">
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={newCustomer.marketingOptIn}
                      onChange={(e) => setNewCustomer(prev => ({
                        ...prev,
                        marketingOptIn: e.target.checked
                      }))}
                    />
                  </FormControl>
                  <FormLabel className="mt-0">Opt-in to marketing</FormLabel>
                </FormItem>
              </FormField>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createCustomerMutation.isLoading}
              >
                {createCustomerMutation.isLoading ? 'Creating...' : 'Create Customer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative max-w-xs">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={filter}
              onValueChange={handleLoyaltyTierChange}
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Filter by tier" />
              </SelectTrigger>
              <SelectContent>
                {LOYALTY_TIERS.map((tier) => (
                  <SelectItem key={tier} value={tier}>
                    {tier === 'ALL' ? 'All Tiers' : tier}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Loyalty Tier</TableHead>
                <TableHead>Orders</TableHead>
                <TableHead>Lifetime Value</TableHead>
                <TableHead>Re-Commerce</TableHead>
                <TableHead>Last Order</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow
                  key={customer.id}
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/admin/customers/${customer.id}`)}
                >
                  <TableCell>
                    {customer.firstName} {customer.lastName}
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>
                    <Badge variant={
                      customer.loyaltyTier === 'VIP' ? 'destructive' :
                        customer.loyaltyTier === 'GOLD' ? 'secondary' :
                          customer.loyaltyTier === 'SILVER' ? 'secondary' : 'outline'
                    }>
                      {customer.loyaltyTier}
                    </Badge>
                  </TableCell>
                  <TableCell>{customer.totalOrders}</TableCell>
                  <TableCell>${customer.lifetimeValue.toFixed(2)}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Text variant="small">
                        {customer.reCommerceActivity.totalReturns} returns
                      </Text>
                      <Text variant="small">
                        {customer.reCommerceActivity.totalRepairs} repairs
                      </Text>
                      <Text variant="small">
                        {customer.reCommerceActivity.totalResales} resales
                      </Text>
                    </div>
                  </TableCell>
                  <TableCell>
                    {customer.lastOrderDate
                      ? new Date(customer.lastOrderDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between mt-4">
            <Text variant="small">
              Showing {startItem} to {endItem} of {totalCount} customers
            </Text>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={!hasPreviousPage}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                disabled={!hasNextPage}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 
